import { isNil } from "lodash";
import {
  type IngestionTestRunQuery,
  ScannedOrderResultStatus,
} from "../../generated/graphql";

/** One authoritative applied result a FULL_APPLY run produced. */
export type AppliedResult =
  IngestionTestRunQuery["ingestionTestRun"]["appliedResults"][number];

/**
 * Classify an applied result into the outcome a reviewer reads at a glance: a
 * DUPLICATE row deduped, a FAILED row produced no order, an order-linked row
 * created. Falls through to the raw status (upper-cased to match the hand-picked
 * labels) so a new status is shown uniformly rather than silently hidden.
 */
export const appliedDecision = (
  result: AppliedResult,
): {
  readonly label: string;
  readonly color: "success" | "warning" | "error" | "default";
} => {
  if (result.status === ScannedOrderResultStatus.Duplicate) {
    return { label: "DEDUP", color: "warning" };
  }
  if (result.status === ScannedOrderResultStatus.Failed) {
    return { label: "FAILED", color: "error" };
  }
  if (!isNil(result.orderUuid)) {
    return { label: "CREATE", color: "success" };
  }
  return { label: result.status.toUpperCase(), color: "default" };
};

/**
 * What the PREVIEW_E2E pane should render: the DRY decision plan once captured,
 * a planning-in-progress note while the run is still in flight, or the
 * failed-before-planning note only for a terminal run that produced no plan.
 * Keeps a still-extracting run from being mislabelled as a planning failure.
 */
export type PreviewPlanView = "plan" | "planning" | "failed";

export const previewPlanView = (input: {
  readonly active: boolean;
  readonly hasPlan: boolean;
}): PreviewPlanView => {
  if (input.hasPlan) {
    return "plan";
  }
  return input.active ? "planning" : "failed";
};

/** One page-bounded apply plan, as the PREVIEW_E2E pane renders it. */
export type PreviewPlanSlice = {
  /**
   * Stable render key. Position-derived rather than bounds-derived because two
   * entries can normalize onto the same bounds when a stored one is malformed,
   * and a duplicate key would drop a shipment from the render.
   */
  readonly key: string;
  /** 0-based inclusive first page of the shipment this plan covers. */
  readonly pageStart: number;
  /** 0-based inclusive last page of the shipment this plan covers. */
  readonly pageEnd: number;
  /** The `planV3Apply` blob, or null when the stored entry carried none. */
  readonly plan: unknown;
};

const pageIndex = (value: unknown): number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0
    ? value
    : 0;

/**
 * Normalize the opaque `apply_plan` blob into one entry per shipment.
 *
 * The column holds TWO shapes and always will: rows written before auto-split
 * store a bare `planV3Apply` object, rows written after store
 * `[{ page_start, page_end, plan }]` — one entry per slice DE cut the file
 * into. Stored rows are never rewritten, so the pane must read both; a bare
 * object is the whole file, which is exactly the `0,0` bounds a single-shipment
 * run records today.
 *
 * Bounds are coerced defensively (anything not a non-negative integer reads
 * `0`) so one malformed entry degrades its own label rather than blanking the
 * pane — an unrendered slice is a shipment the reviewer never learns about.
 */
export const normalizePreviewPlans = (
  applyPlan: unknown,
): PreviewPlanSlice[] => {
  if (isNil(applyPlan)) {
    return [];
  }
  if (!Array.isArray(applyPlan)) {
    return [{ key: "0", pageStart: 0, pageEnd: 0, plan: applyPlan }];
  }
  return applyPlan.map((entry, index) => {
    // `null` and primitives both reach here from stored JSON, and reading
    // `.page_start` off `null` THROWS — which would blank the whole pane, the
    // one outcome this normalizer exists to prevent. Fall back to the empty
    // slice so a bad entry costs its own label and nothing else.
    const slice = (
      typeof entry === "object" && entry !== null ? entry : {}
    ) as {
      readonly page_start?: unknown;
      readonly page_end?: unknown;
      readonly plan?: unknown;
    };
    return {
      key: String(index),
      pageStart: pageIndex(slice.page_start),
      pageEnd: pageIndex(slice.page_end),
      plan: slice.plan ?? null,
    };
  });
};

/**
 * Label a 0-based inclusive page range the way the rest of this tool shows
 * stored values — raw, so it greps against `extractedFields`, the
 * `scanned_order_results` rows, and DE's logs without an off-by-one.
 */
export const pageRangeLabel = (bounds: {
  readonly pageStart: number;
  readonly pageEnd: number;
}): string =>
  bounds.pageStart === bounds.pageEnd
    ? `page ${bounds.pageStart}`
    : `pages ${bounds.pageStart}–${bounds.pageEnd}`;
